import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRocket, faSearch, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { useApiDocs } from '../../components/apidocs';
import { API_DOCS_TABS, API_DOCS_CATEGORIES } from './navigation';

type Props = {
  activeTab: string;
  onSelectTab: (id: string) => void;
  /** เปิดอยู่หรือไม่บนจอมือถือ (จอใหญ่แสดงตลอด) */
  open: boolean;
};

const ApiDocsSidebar: React.FC<Props> = ({ activeTab, onSelectTab, open }) => {
  const { apiVersion, setApiVersion } = useApiDocs();
  const [search, setSearch] = useState('');

  return (
    <aside className={`
      ${open ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0 lg:translate-x-0 lg:opacity-100'}
      fixed lg:relative z-30 lg:z-auto inset-y-0 left-0 lg:inset-auto
      w-72 lg:w-56 xl:w-64 shrink-0
      bg-white dark:bg-slate-800 lg:bg-white/80 lg:dark:bg-slate-800/80 lg:backdrop-blur-xl
      border-r lg:border border-gray-200/80 dark:border-slate-700
      lg:rounded-2xl lg:shadow-sm
      transition-all duration-300 ease-out
      overflow-y-auto lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-2rem)]
    `}>
      <div className="p-4">
        {/* Sidebar header */}
        <div className="flex items-center gap-2 px-3 mb-5">
          <div
            className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs"
            style={{ background: 'var(--theme-primary-gradient)' }}
          >
            <FontAwesomeIcon icon={faRocket} />
          </div>
          <span className="font-bold text-gray-800 dark:text-slate-100 text-sm tracking-tight">API Reference</span>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 text-xs" />
          <input
            type="text"
            placeholder="Search docs..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700 dark:text-slate-100 focus:bg-white dark:focus:bg-slate-700 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 dark:focus:ring-slate-600 outline-none transition-all"
          />
        </div>

        {/* Navigation grouped by category */}
        <nav className="space-y-5">
          {API_DOCS_CATEGORIES.map(category => {
            const filteredTabs = API_DOCS_TABS.filter(tab => tab.category === category && (!search || tab.label.toLowerCase().includes(search.toLowerCase())));
            if (filteredTabs.length === 0) return null;
            return (
              <div key={category}>
                <div className="px-3 mb-2 text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-[0.15em]">
                  {category}
                </div>
                <div className="space-y-0.5">
                  {filteredTabs.map(tab => (
                    <button
                      key={tab.id}
                      className={`api-docs-sidebar-item w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        activeTab === tab.id
                          ? 'text-white shadow-md'
                          : 'text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-gray-800 dark:hover:text-slate-100'
                      }`}
                      style={activeTab === tab.id ? { background: 'var(--theme-primary-gradient)' } : {}}
                      onClick={() => onSelectTab(tab.id)}
                    >
                      <FontAwesomeIcon icon={tab.icon} className={`text-xs w-4 ${activeTab === tab.id ? '' : 'opacity-60'}`} />
                      <span>{tab.label}</span>
                      {activeTab === tab.id && (
                        <FontAwesomeIcon icon={faChevronRight} className="ml-auto text-[10px] opacity-60" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        {/* Sidebar footer — API version */}
        <div className="mt-6 mx-3 p-3 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-700/50 dark:to-slate-700/30 rounded-xl border border-gray-200/50 dark:border-slate-700">
          <div className="text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-1">API Version</div>
          <div className="flex gap-1">
            {(['v2', 'v1'] as const).map(v => (
              <button
                key={v}
                className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  apiVersion === v
                    ? v === 'v2' ? 'bg-emerald-500 text-white shadow-sm' : 'bg-gray-500 text-white shadow-sm'
                    : 'text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300'
                }`}
                onClick={() => setApiVersion(v)}
              >
                {v.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>
    </aside>
  );
};

export default ApiDocsSidebar;
